import React, { useMemo } from 'react';
import { SystemCard } from '../components/SystemCard';
import { SystemCardPopoverContent } from '../components/SystemCardPopoverContent';
import { LightingWithMetrics } from '../types';
import { iconForEnergySink, isPassiveTechnology } from '../utils';

const LIGHTING_SYSTEM_IMAGE = (
  <svg
    width='1em'
    height='1em'
    viewBox='0 0 61 61'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
  >
    <path
      d='M32.7397 16.3665V5.01167C32.7397 3.86218 31.8102 2.93262 30.6609 2.93262C29.5115 2.93262 28.5821 3.86218 28.5821 5.01167V16.3665C21.3261 16.8913 14.5402 20.1448 9.58565 25.4696C4.63099 30.7971 1.87744 37.8013 1.87744 45.0754C1.87744 45.6276 2.09732 46.1549 2.48709 46.5447C2.87687 46.9321 3.40656 47.1495 3.95626 47.1495H15.375C16.0496 52.1222 19.1028 56.4553 23.5578 58.7666C28.0102 61.078 33.3122 61.078 37.7647 58.7666C42.2196 56.4551 45.2727 52.1219 45.9475 47.1495H57.3662C57.9159 47.1495 58.4456 46.9321 58.8354 46.5447C59.2251 46.1549 59.445 45.6277 59.445 45.0754C59.445 37.8013 56.6916 30.7971 51.7368 25.4696C46.7821 20.1445 39.9964 16.8911 32.7404 16.3665H32.7397ZM30.6609 56.356C28.0324 56.351 25.4864 55.429 23.4625 53.7497C21.4411 52.068 20.0669 49.7341 19.5822 47.1505H41.7397C41.255 49.7343 39.8808 52.0682 37.8594 53.7497C35.8356 55.429 33.2895 56.351 30.6609 56.356Z'
      fill='currentColor'
    />
  </svg>
);

type LightingUnitProps = {
  lightingUnit: LightingWithMetrics;
  selected: boolean;
  count?: number;
  isLoading?: boolean;
  readOnly?: boolean;
  actions: {
    onAdd?: () => void;
    onIncrement?: () => void;
    onDecrement?: () => void;
    onRemove?: () => void;
    onChangeCount?: (newCount: number) => void;
  };
};

export const LightingUnit: React.FC<LightingUnitProps> = React.memo(
  ({ lightingUnit, selected, count, isLoading, actions, readOnly }) => {
    const EnergyIcon = iconForEnergySink(lightingUnit.energySink);

    const icons = useMemo(
      () => ({
        EnergyIcon: EnergyIcon,
        energyIconCount: lightingUnit.relativeEnergyDemand,
        costIconCount: lightingUnit.relativeCost,
      }),
      [
        EnergyIcon,
        lightingUnit.relativeEnergyDemand,
        lightingUnit.relativeCost,
      ],
    );

    const hoverPopover = useMemo(
      () => (
        <SystemCardPopoverContent
          coveragePercentage={lightingUnit.coveragePercentage}
          name={lightingUnit.name}
          energySource={lightingUnit.energySink}
          EnergySourceIcon={EnergyIcon}
          energyDemand={Number(lightingUnit.energyUsePerYr)}
          capitalCost={Number(lightingUnit.capitalCostPerUnit)}
          annualCost={Number(lightingUnit.maintenanceCostPerUnit)}
          coverageFieldTitle={'lighting'}
          designLife={lightingUnit.designLife}
          isLoading={isLoading}
        />
      ),
      [lightingUnit, EnergyIcon, isLoading],
    );

    return (
      <SystemCard
        actions={actions}
        count={count}
        limit={lightingUnit.limit}
        name={lightingUnit.name}
        selected={selected}
        systemImage={LIGHTING_SYSTEM_IMAGE}
        icons={icons}
        hoverPopover={hoverPopover}
        readOnly={readOnly}
        unitsFixed={isPassiveTechnology(lightingUnit)}
      />
    );
  },
);

LightingUnit.displayName = 'LightingUnit';
